<template>
  <div class="wa-ai-settings">
    <!-- Header -->
    <div class="wa-ai-settings__header">
      <h3 class="wa-ai-settings__title">⚙️ IA Autônoma — Configurações</h3>
      <p class="wa-ai-settings__subtitle">
        Gerencie os parâmetros dos bots de IA para a instância
        <strong>{{ instanceName }}</strong>
      </p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="wa-ai-settings__loading">
      <span class="spinner"></span> Carregando configurações...
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="wa-ai-settings__error">
      <p>{{ error }}</p>
      <button class="wa-ai-btn wa-ai-btn--secondary" @click="loadSettings">
        Tentar novamente
      </button>
    </div>

    <!-- Content -->
    <div v-else class="wa-ai-settings__content">
      <!-- No integrations -->
      <div v-if="activeIntegrations.length === 0" class="wa-ai-settings__empty">
        Nenhuma integração de IA configurada para esta instância.
      </div>

      <!-- Integration sections -->
      <div
        v-for="integration in activeIntegrations"
        :key="integration.name"
        class="wa-ai-settings__section"
      >
        <button
          class="wa-ai-settings__section-header"
          :class="{ 'wa-ai-settings__section-header--open': openSections[integration.name] }"
          @click="toggleSection(integration.name)"
        >
          <span class="wa-ai-settings__section-icon">{{ integration.icon }}</span>
          <span class="wa-ai-settings__section-title">{{ integration.label }}</span>
          <span class="wa-ai-settings__section-badge">
            {{ integration.bots.length }} bot{{ integration.bots.length !== 1 ? 's' : '' }}
          </span>
          <span class="wa-ai-settings__chevron">▼</span>
        </button>

        <div v-show="openSections[integration.name]" class="wa-ai-settings__section-body">
          <!-- Per-bot cards -->
          <div
            v-for="bot in integration.bots"
            :key="bot.id"
            class="wa-ai-settings__bot-card"
          >
            <div class="wa-ai-settings__bot-header">
              <span class="wa-ai-settings__bot-name">
                {{ bot.description || bot.botType || 'Bot ' + bot.id.slice(0, 8) }}
              </span>
              <label class="wa-ai-toggle">
                <input
                  type="checkbox"
                  :checked="bot.enabled !== false"
                  @change="updateField(integration.name, bot.id, 'enabled', $event.target.checked)"
                />
                <span class="wa-ai-toggle__slider"></span>
              </label>
            </div>

            <!-- General fields -->
            <div class="wa-ai-settings__fields">
              <h4 class="wa-ai-settings__fields-title">Comportamento geral</h4>

              <div class="wa-ai-field">
                <label>Trigger</label>
                <select
                  :value="bot.triggerType || 'all'"
                  @change="updateField(integration.name, bot.id, 'triggerType', $event.target.value)"
                >
                  <option value="all">Todas as mensagens</option>
                  <option value="keyword">Palavra-chave</option>
                  <option value="none">Desabilitado</option>
                  <option value="advanced">Avançado</option>
                </select>
              </div>

              <div v-if="bot.triggerType === 'keyword'" class="wa-ai-field">
                <label>Palavra-chave</label>
                <input
                  type="text"
                  :value="bot.triggerValue || ''"
                  @blur="updateField(integration.name, bot.id, 'triggerValue', $event.target.value)"
                />
              </div>

              <div class="wa-ai-field wa-ai-field--row">
                <div class="wa-ai-field">
                  <label>Delay (ms)</label>
                  <input
                    type="number"
                    :value="bot.delayMessage || 0"
                    min="0"
                    max="30000"
                    @blur="updateField(integration.name, bot.id, 'delayMessage', Number($event.target.value))"
                  />
                </div>
                <div class="wa-ai-field">
                  <label>Debounce (ms)</label>
                  <input
                    type="number"
                    :value="bot.debounceTime || 0"
                    min="0"
                    max="60000"
                    @blur="updateField(integration.name, bot.id, 'debounceTime', Number($event.target.value))"
                  />
                </div>
                <div class="wa-ai-field">
                  <label>Expiração (ms)</label>
                  <input
                    type="number"
                    :value="bot.expire || 0"
                    min="0"
                    @blur="updateField(integration.name, bot.id, 'expire', Number($event.target.value))"
                  />
                </div>
              </div>

              <div class="wa-ai-field">
                <label>Mensagem desconhecida</label>
                <input
                  type="text"
                  :value="bot.unknownMessage || ''"
                  placeholder="Mensagem quando o bot não entende..."
                  @blur="updateField(integration.name, bot.id, 'unknownMessage', $event.target.value)"
                />
              </div>

              <div class="wa-ai-field">
                <label>Palavra de encerramento</label>
                <input
                  type="text"
                  :value="bot.keywordFinish || ''"
                  placeholder="Ex: #sair"
                  @blur="updateField(integration.name, bot.id, 'keywordFinish', $event.target.value)"
                />
              </div>

              <div class="wa-ai-field wa-ai-field--checkboxes">
                <label>
                  <input type="checkbox" :checked="bot.keepOpen" @change="updateField(integration.name, bot.id, 'keepOpen', $event.target.checked)" />
                  Manter sessão aberta
                </label>
                <label>
                  <input type="checkbox" :checked="bot.splitMessages" @change="updateField(integration.name, bot.id, 'splitMessages', $event.target.checked)" />
                  Dividir mensagens longas
                </label>
                <label>
                  <input type="checkbox" :checked="bot.listeningFromMe" @change="updateField(integration.name, bot.id, 'listeningFromMe', $event.target.checked)" />
                  Ouvir próprias msgs
                </label>
                <label>
                  <input type="checkbox" :checked="bot.stopBotFromMe" @change="updateField(integration.name, bot.id, 'stopBotFromMe', $event.target.checked)" />
                  Parar se eu enviar
                </label>
              </div>
            </div>

            <!-- Integration-specific fields -->
            <div v-if="integration.name === 'openai'" class="wa-ai-settings__fields">
              <h4 class="wa-ai-settings__fields-title">OpenAI / ChatGPT</h4>

              <div class="wa-ai-field">
                <label>Modelo</label>
                <select
                  :value="bot.model || 'gpt-4o-mini'"
                  @change="updateField(integration.name, bot.id, 'model', $event.target.value)"
                >
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  <option value="o1-preview">O1 Preview</option>
                  <option value="o1-mini">O1 Mini</option>
                </select>
              </div>

              <div class="wa-ai-field">
                <label>Max Tokens</label>
                <input
                  type="number"
                  :value="bot.maxTokens || 1000"
                  min="1"
                  max="128000"
                  @blur="updateField(integration.name, bot.id, 'maxTokens', Number($event.target.value))"
                />
              </div>

              <div class="wa-ai-field">
                <label>System Prompt</label>
                <textarea
                  rows="6"
                  :value="formatSystemMessages(bot.systemMessages)"
                  placeholder="Instrução do sistema (uma por linha)..."
                  @blur="updateField(integration.name, bot.id, 'systemMessages', parseSystemMessages($event.target.value))"
                ></textarea>
              </div>

              <div class="wa-ai-field">
                <label>Assistant ID (opcional)</label>
                <input
                  type="text"
                  :value="bot.assistantId || ''"
                  placeholder="asst_..."
                  @blur="updateField(integration.name, bot.id, 'assistantId', $event.target.value)"
                />
              </div>

              <div class="wa-ai-field wa-ai-field--checkboxes">
                <label>
                  <input type="checkbox" :checked="bot.speechToText" @change="updateField(integration.name, bot.id, 'speechToText', $event.target.checked)" />
                  Speech-to-Text (transcrição de áudio)
                </label>
              </div>
            </div>

            <div v-if="integration.name === 'dify'" class="wa-ai-settings__fields">
              <h4 class="wa-ai-settings__fields-title">Dify</h4>
              <div class="wa-ai-field">
                <label>Tipo</label>
                <select
                  :value="bot.botType || 'chatBot'"
                  @change="updateField(integration.name, bot.id, 'botType', $event.target.value)"
                >
                  <option value="chatBot">Chat Bot</option>
                  <option value="textGenerator">Text Generator</option>
                  <option value="agent">Agent</option>
                  <option value="workflow">Workflow</option>
                </select>
              </div>
              <div class="wa-ai-field">
                <label>API URL</label>
                <input
                  type="url"
                  :value="bot.apiUrl || ''"
                  @blur="updateField(integration.name, bot.id, 'apiUrl', $event.target.value)"
                />
              </div>
            </div>

            <div v-if="integration.name === 'n8n'" class="wa-ai-settings__fields">
              <h4 class="wa-ai-settings__fields-title">N8N</h4>
              <div class="wa-ai-field">
                <label>Webhook URL</label>
                <input
                  type="url"
                  :value="bot.webhookUrl || ''"
                  @blur="updateField(integration.name, bot.id, 'webhookUrl', $event.target.value)"
                />
              </div>
            </div>

            <div v-if="integration.name === 'typebot'" class="wa-ai-settings__fields">
              <h4 class="wa-ai-settings__fields-title">Typebot</h4>
              <div class="wa-ai-field">
                <label>URL</label>
                <input type="url" :value="bot.url || ''" @blur="updateField(integration.name, bot.id, 'url', $event.target.value)" />
              </div>
              <div class="wa-ai-field">
                <label>Typebot (slug)</label>
                <input type="text" :value="bot.typebot || ''" @blur="updateField(integration.name, bot.id, 'typebot', $event.target.value)" />
              </div>
            </div>

            <div v-if="integration.name === 'evoai'" class="wa-ai-settings__fields">
              <h4 class="wa-ai-settings__fields-title">EvoAI</h4>
              <div class="wa-ai-field">
                <label>Agent URL</label>
                <input type="url" :value="bot.agentUrl || ''" @blur="updateField(integration.name, bot.id, 'agentUrl', $event.target.value)" />
              </div>
            </div>

            <!-- Save status -->
            <div v-if="saveStatus[bot.id]" class="wa-ai-settings__save-status" :class="'wa-ai-settings__save-status--' + saveStatus[bot.id].type">
              {{ saveStatus[bot.id].message }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchAiSettings, updateAiBot } from '../api/aiSettingsApi';

const INTEGRATION_META = {
  openai: { label: 'OpenAI (ChatGPT)', icon: '🤖' },
  dify: { label: 'Dify (Agent/Workflow)', icon: '🔄' },
  n8n: { label: 'N8N (Automação)', icon: '⚙️' },
  typebot: { label: 'Typebot', icon: '💬' },
  evoai: { label: 'EvoAI', icon: '🎯' },
  flowise: { label: 'Flowise', icon: '🌊' },
  evolutionBot: { label: 'Evolution Bot', icon: '🦾' },
};

export default {
  name: 'WaAiSettings',

  props: {
    instanceName: { type: String, required: true },
    authToken: { type: String, default: '' },
  },

  data() {
    return {
      loading: false,
      error: null,
      rawData: null,
      openSections: {},
      saveStatus: {},
      saveTimers: {},
    };
  },

  computed: {
    activeIntegrations() {
      if (!this.rawData?.integrations) return [];
      return Object.entries(this.rawData.integrations)
        .filter(([, data]) => {
          const bots = Array.isArray(data) ? data : [data];
          return bots.length > 0;
        })
        .map(([name, data]) => ({
          name,
          label: INTEGRATION_META[name]?.label || name,
          icon: INTEGRATION_META[name]?.icon || '🔌',
          bots: Array.isArray(data) ? data : [data],
        }));
    },
  },

  watch: {
    instanceName: {
      immediate: true,
      handler(val) { if (val) this.loadSettings(); },
    },
  },

  methods: {
    async loadSettings() {
      this.loading = true;
      this.error = null;
      try {
        this.rawData = await fetchAiSettings(this.instanceName, this.authToken);
        // Open first section by default
        if (this.activeIntegrations.length > 0) {
          this.$set
            ? this.$set(this.openSections, this.activeIntegrations[0].name, true)
            : (this.openSections[this.activeIntegrations[0].name] = true);
        }
      } catch (err) {
        if (err.status === 403) {
          this.error = 'Acesso negado. Apenas administradores podem gerenciar IA.';
        } else if (err.status === 401) {
          this.error = 'Sessão expirada. Faça login novamente.';
        } else {
          this.error = `Erro ao carregar: ${err.message}`;
        }
      } finally {
        this.loading = false;
      }
    },

    toggleSection(name) {
      this.$set
        ? this.$set(this.openSections, name, !this.openSections[name])
        : (this.openSections[name] = !this.openSections[name]);
    },

    async updateField(integration, botId, field, value) {
      // Debounced save
      const key = `${botId}:${field}`;
      if (this.saveTimers[key]) clearTimeout(this.saveTimers[key]);

      this.saveTimers[key] = setTimeout(async () => {
        this.$set
          ? this.$set(this.saveStatus, botId, { type: 'saving', message: 'Salvando...' })
          : (this.saveStatus[botId] = { type: 'saving', message: 'Salvando...' });

        try {
          await updateAiBot(this.instanceName, integration, botId, { [field]: value }, this.authToken);

          // Update local data
          const intData = this.rawData.integrations[integration];
          const bots = Array.isArray(intData) ? intData : [intData];
          const bot = bots.find(b => b.id === botId);
          if (bot) bot[field] = value;

          this.$set
            ? this.$set(this.saveStatus, botId, { type: 'success', message: '✓ Salvo' })
            : (this.saveStatus[botId] = { type: 'success', message: '✓ Salvo' });

          setTimeout(() => {
            this.$set
              ? this.$set(this.saveStatus, botId, null)
              : (this.saveStatus[botId] = null);
          }, 2000);
        } catch (err) {
          this.$set
            ? this.$set(this.saveStatus, botId, { type: 'error', message: `Erro: ${err.message}` })
            : (this.saveStatus[botId] = { type: 'error', message: `Erro: ${err.message}` });
        }
      }, 600);
    },

    formatSystemMessages(msgs) {
      if (!msgs) return '';
      if (Array.isArray(msgs)) return msgs.join('\n');
      return String(msgs);
    },

    parseSystemMessages(text) {
      if (!text) return [];
      return text.split('\n').filter(Boolean);
    },
  },
};
</script>

<style scoped>
.wa-ai-settings {
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.wa-ai-settings__header {
  margin-bottom: 20px;
}

.wa-ai-settings__title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: var(--color-heading, #1f2d3d);
}

.wa-ai-settings__subtitle {
  font-size: 13px;
  color: var(--s-600, #6e7c87);
  margin: 0;
}

.wa-ai-settings__loading {
  text-align: center;
  padding: 40px;
  color: var(--s-500, #8c9196);
}

.spinner {
  display: inline-block;
  width: 16px; height: 16px;
  border: 2px solid var(--w-500, #1f93ff);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.wa-ai-settings__error {
  background: var(--r-50, #fff0f0);
  border: 1px solid var(--r-200, #ffc7c7);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  color: var(--r-800, #c72222);
}

.wa-ai-settings__empty {
  text-align: center;
  padding: 40px;
  color: var(--s-500, #8c9196);
}

/* Sections */
.wa-ai-settings__section {
  border: 1px solid var(--s-100, #ebf0f5);
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.wa-ai-settings__section-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--s-25, #f8fafc);
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  transition: background 0.15s;
}

.wa-ai-settings__section-header:hover {
  background: var(--s-50, #f1f5f8);
}

.wa-ai-settings__section-icon { font-size: 18px; }

.wa-ai-settings__section-title { flex: 1; }

.wa-ai-settings__section-badge {
  font-size: 11px;
  background: var(--w-100, #e3f2ff);
  color: var(--w-700, #0070cc);
  padding: 2px 8px;
  border-radius: 10px;
}

.wa-ai-settings__chevron {
  font-size: 10px;
  transition: transform 0.2s;
  color: var(--s-400, #a8b8c7);
}

.wa-ai-settings__section-header--open .wa-ai-settings__chevron {
  transform: rotate(180deg);
}

.wa-ai-settings__section-body {
  padding: 12px;
}

/* Bot cards */
.wa-ai-settings__bot-card {
  border: 1px solid var(--s-75, #f0f4f7);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  background: white;
}

.wa-ai-settings__bot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.wa-ai-settings__bot-name {
  font-weight: 500;
  font-size: 14px;
}

/* Toggle switch */
.wa-ai-toggle {
  position: relative;
  display: inline-block;
  width: 36px; height: 20px;
}

.wa-ai-toggle input { opacity: 0; width: 0; height: 0; }

.wa-ai-toggle__slider {
  position: absolute;
  inset: 0;
  background: var(--s-200, #d6dfe8);
  border-radius: 20px;
  transition: background 0.2s;
  cursor: pointer;
}

.wa-ai-toggle__slider::before {
  content: '';
  position: absolute;
  width: 16px; height: 16px;
  left: 2px; top: 2px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
}

.wa-ai-toggle input:checked + .wa-ai-toggle__slider {
  background: var(--g-500, #44ce4b);
}

.wa-ai-toggle input:checked + .wa-ai-toggle__slider::before {
  transform: translateX(16px);
}

/* Fields */
.wa-ai-settings__fields {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--s-50, #f1f5f8);
}

.wa-ai-settings__fields-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--s-500, #8c9196);
  margin: 0 0 10px;
  letter-spacing: 0.5px;
}

.wa-ai-field {
  margin-bottom: 10px;
}

.wa-ai-field label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: var(--s-700, #3c4858);
  margin-bottom: 4px;
}

.wa-ai-field input[type="text"],
.wa-ai-field input[type="url"],
.wa-ai-field input[type="number"],
.wa-ai-field select,
.wa-ai-field textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--s-200, #d6dfe8);
  border-radius: 6px;
  font-size: 13px;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.wa-ai-field input:focus,
.wa-ai-field select:focus,
.wa-ai-field textarea:focus {
  outline: none;
  border-color: var(--w-500, #1f93ff);
  box-shadow: 0 0 0 2px rgba(31, 147, 255, 0.1);
}

.wa-ai-field textarea {
  resize: vertical;
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  line-height: 1.5;
}

.wa-ai-field--row {
  display: flex;
  gap: 10px;
}

.wa-ai-field--row .wa-ai-field {
  flex: 1;
}

.wa-ai-field--checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.wa-ai-field--checkboxes label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}

/* Save status */
.wa-ai-settings__save-status {
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 4px;
  margin-top: 8px;
  text-align: right;
}

.wa-ai-settings__save-status--saving { color: var(--s-600, #6e7c87); }
.wa-ai-settings__save-status--success { color: var(--g-600, #36a63e); }
.wa-ai-settings__save-status--error { color: var(--r-600, #e03131); }

/* Buttons */
.wa-ai-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.wa-ai-btn--secondary {
  background: var(--s-100, #ebf0f5);
  color: var(--s-700, #3c4858);
}

.wa-ai-btn--secondary:hover {
  background: var(--s-200, #d6dfe8);
}
</style>
